'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { axiosInstance } from '@/lib/axios';
import Image from 'next/image';

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const { setUser, clearUser, setLoading } = useAuthStore();
    const [isInitialized, setIsInitialized] = useState(false);
    const pathname = usePathname();

    useEffect(() => {
        let mounted = true;

        const initAuth = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/auth/me');
                // Support both { user } and { data: { user } } structures
                const userData = response.data?.user || response.data?.data?.user;

                if (mounted && userData) {
                    setUser(userData);
                } else if (mounted) {
                    clearUser();
                }
            } catch (err) {
                console.error("Auth initialization failed:", err);
                if (mounted) {
                    clearUser();
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                    setIsInitialized(true);
                }
            }
        };

        initAuth();

        return () => {
            mounted = false;
        };
    }, [setUser, clearUser, setLoading]);

    // 🔹 Ensure the application stays in a loading state *only* for protected routes
    // This allows the Landing Page to be instant while protecting private areas.
    const isProtectedRoute = pathname?.startsWith('/admin') || pathname?.startsWith('/dashboard');

    if (!isInitialized && isProtectedRoute) {
        return (
            <div className="fixed inset-0 z-9999 flex flex-col items-center justify-center bg-white">
                <div className="relative">
                    <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary-100 border-t-primary-600" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Image
                            src="/assets/logo/logo.png"
                            alt="Logo"
                            width={50}
                            height={50}
                            className="object-contain"
                        />
                    </div>
                </div>
                <p className="mt-6 font-brastika text-lg animate-pulse text-primary-900">
                    Chargement de votre session...
                </p>
                <p className="mt-2 text-xs text-primary-400 font-medium tracking-widest uppercase">
                    Ezzahraoui
                </p>
            </div>
        );
    }

    return <>{children}</>;
}
